import React, { type FC, useEffect, useMemo, useState } from 'react'
import { LoadingButton } from '@mui/lab'
import { CircularProgress, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { Link, useHref, useLocation, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { type InferType, object, string, ref } from 'yup'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { ReactComponent as Eye } from 'common/assets/eye.svg'
import { ReactComponent as EyeCrossed } from 'common/assets/eyeCrossed.svg'
import { StoreStatus, UserApps } from 'common/utils/types'
import styles from 'common/navigation/auth-layout/AuthLayout.module.scss'

import { forgotPassword, resetPassword } from '../auth.service'
import { resetStatus } from '../auth.slice'
import Message from '../Message'

const resetPasswordSchema = object({
  email: string().email('Invalidan email').required('Obavezno polje').default(''),
  code: string().required('Obavezno polje').default(''),
  password: string()
    .required('Obavezno polje')
    .default('')
    .min(
      8,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    )
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[~!@#$%^&*_\-+=`|\\(){}[\]:;"'<>,.?/]).*$/,
      'Lozinka mora sadržati minimum 8 karaktera, jedno veliko slovo, jedno malo slovo, broj i specijalni karakter',
    ),
  confirmPassword: string()
    .required('Obavezno polje')
    .default('')
    .oneOf([ref('password')], 'Lozinke moraju biti iste'),
})
export type ResetPasswordForm = InferType<typeof resetPasswordSchema>

const ResetPassword: FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const activeAppRoute = useHref('/')
  const { resetPasswordStatus, status } = useAppSelector((state) => state.auth)
  const [showPassword, setShowPassword] = useState(false)

  const email: string = useMemo(() => (location.state as Record<string, string>)?.email || '', [])

  const defaultValues: ResetPasswordForm = {
    email,
    code: '',
    password: '',
    confirmPassword: '',
  }

  useEffect(() => {
    if (!email) {
      navigate('/', { replace: true })
    }
  }, [])

  useEffect(() => {
    if (resetPasswordStatus === StoreStatus.Succeeded) {
      navigate(
        `/auth${activeAppRoute === UserApps.Vendors ? '/login/' : ''}?email=${encodeURIComponent(
          formik.values.email,
        )}&password=${encodeURIComponent(formik.values.password)}`,
      )
      dispatch(resetStatus())
    }
  }, [dispatch, navigate, resetPasswordStatus])

  const formik = useFormik({
    initialValues: defaultValues,
    validationSchema: resetPasswordSchema,
    onSubmit: async (values: ResetPasswordForm) => await dispatch(resetPassword(values)),
  })

  return (
    <div className={styles.container}>
      <h2 className="text-center">Reset password</h2>

      <Message />

      <form onSubmit={formik.handleSubmit} className={styles.formContainer}>
        <TextField
          id="email"
          name="email"
          label="Email"
          placeholder="name@example.com"
          type="email"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          error={formik.touched.email && !!formik.errors.email}
          helperText={formik.touched.email && formik.errors.email}
          sx={{
            width: '100%',
          }}
          InputProps={{
            readOnly: true,
            classes: {
              root: styles.input,
            },
          }}
        />
        <TextField
          id="code"
          name="code"
          label="Code"
          placeholder="XXXXXX"
          type="text"
          autoFocus={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.code}
          error={formik.touched.code && !!formik.errors.code}
          helperText={formik.touched.code && formik.errors.code}
          sx={{
            width: '100%',
          }}
          InputProps={{
            classes: {
              root: styles.input,
            },
          }}
        />
        <TextField
          id="password"
          name="password"
          label="Lozinka"
          type={showPassword ? 'text' : 'password'}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          error={formik.touched.password && !!formik.errors.password}
          helperText={formik.touched.password && formik.errors.password}
          sx={{
            width: '100%',
          }}
          InputProps={{
            classes: {
              root: styles.input,
            },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={(e) => {
                    e.preventDefault()
                    setShowPassword((show) => !show)
                    document.getElementById('password')?.focus()
                  }}
                  edge="end"
                >
                  {showPassword ? <Eye /> : <EyeCrossed />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          id="confirmPassword"
          name="confirmPassword"
          label="Ponovi lozinku"
          type={showPassword ? 'text' : 'password'}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.confirmPassword}
          error={formik.touched.confirmPassword && !!formik.errors.confirmPassword}
          helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
          sx={{
            width: '100%',
          }}
          InputProps={{
            classes: {
              root: styles.input,
            },
          }}
        />

        <LoadingButton
          type="submit"
          loading={resetPasswordStatus === StoreStatus.Loading}
          variant="outlined"
          sx={{
            width: 200,
          }}
        >
          Potvrdi
        </LoadingButton>
      </form>
      {status === StoreStatus.Loading ? (
        <CircularProgress />
      ) : (
        <Typography variant="body1">
          Niste primili mejl sa kodom?{' '}
          <Link to="#" onClick={() => void dispatch(forgotPassword(defaultValues))}>
            Pošalji opet
          </Link>
        </Typography>
      )}
    </div>
  )
}

export default ResetPassword
